/** Pure numeric artwork shared by the editor and the generated loading sprite.
 * No renderer, simulator, schema or browser dependency. */
export const WIRE_PIXEL_GRID = 25;
export type WireRect = readonly [number, number, number, number];
const center = (WIRE_PIXEL_GRID - 1) / 2;
const point = (x: number, y: number) => y * WIRE_PIXEL_GRID + x;
const countPorts = (mask: number) => [0, 1, 2, 3].filter((i) => mask & (1 << i)).length;

// TR, RB, BL, LT. The same small corner profile is reflected into each quadrant.
const quadrants = [
  [1, -1],
  [1, 1],
  [-1, 1],
  [-1, -1],
] as const;

function square(pixels: Uint8Array, lo: number, hi: number) {
  for (let y = lo; y <= hi; y++) for (let x = lo; x <= hi; x++) pixels[point(x, y)] = 1;
}

/** A local stepped fillet, never a broad sweep across the cell. */
function corner(
  pixels: Uint8Array,
  quadrant: number,
  lo: number,
  hi: number,
  radius: number,
  inside: boolean,
) {
  const [sx, sy] = quadrants[quadrant];
  for (let dy = 0; dy < radius; dy++)
    for (let dx = 0; dx < radius - dy; dx++) {
      const x = inside
        ? sx > 0
          ? hi - dx
          : lo + dx
        : sx > 0
          ? hi + 1 + dx
          : lo - 1 - dx;
      const y = inside
        ? sy > 0
          ? hi - dy
          : lo + dy
        : sy > 0
          ? hi + 1 + dy
          : lo - 1 - dy;
      pixels[point(x, y)] = inside ? 0 : 1;
    }
}

function topology(mask: number, width: number, radius: number) {
  const pixels = new Uint8Array(WIRE_PIXEL_GRID ** 2);
  const lo = center - (width - 1) / 2,
    hi = center + (width - 1) / 2;
  if (!mask) {
    square(pixels, lo - 1, hi + 1);
    for (let q = 0; q < 4; q++) corner(pixels, q, lo - 1, hi + 1, radius, true);
    return pixels;
  }
  square(pixels, lo, hi);
  for (let y = 0; y < WIRE_PIXEL_GRID; y++)
    for (let x = 0; x < WIRE_PIXEL_GRID; x++)
      if (
        (mask & 1 && x >= lo && x <= hi && y < lo) ||
        (mask & 2 && y >= lo && y <= hi && x > hi) ||
        (mask & 4 && x >= lo && x <= hi && y > hi) ||
        (mask & 8 && y >= lo && y <= hi && x < lo)
      )
        pixels[point(x, y)] = 1;
  for (let q = 0; q < 4; q++) {
    const pair = (1 << q) | (1 << ((q + 1) % 4));
    // Every adjacent active pair has the same inner curve: bends, Ts and crosses.
    if ((mask & pair) === pair) corner(pixels, q, lo, hi, radius, false);
    // A bend also has one convex outer corner. Connected edges stay square.
    if (countPorts(mask) === 2 && (mask & pair) === 0)
      corner(pixels, q, lo, hi, radius, true);
  }
  return pixels;
}

/** Only exposed scalar-wire terminals are rounded. Ports, bends and junctions
 * keep their existing masks; bundled cables keep their separate artwork. */
function wireTopology(mask: number) {
  const pixels = topology(mask, 9, 2);
  if (countPorts(mask) > 1) return pixels;
  const radius = mask ? 4.5 : 5.5;
  for (let y = 0; y < WIRE_PIXEL_GRID; y++)
    for (let x = 0; x < WIRE_PIXEL_GRID; x++) {
      const exposed =
        !mask ||
        (mask === 1 && y > center) ||
        (mask === 2 && x < center) ||
        (mask === 4 && y < center) ||
        (mask === 8 && x > center);
      if (exposed && (x - center) ** 2 + (y - center) ** 2 > radius ** 2)
        pixels[point(x, y)] = 0;
    }
  return pixels;
}

function stripe(mask: number) {
  const pixels = new Uint8Array(WIRE_PIXEL_GRID ** 2);
  if (!mask) {
    square(pixels, center - 1, center + 1);
    return pixels;
  }
  pixels[point(center, center)] = 1;
  for (let i = 0; i < WIRE_PIXEL_GRID; i++) {
    if ((mask & 1 && i < center) || (mask & 4 && i > center))
      pixels[point(center, i)] = 1;
    if ((mask & 8 && i < center) || (mask & 2 && i > center))
      pixels[point(i, center)] = 1;
  }
  if (countPorts(mask) === 2) {
    for (let q = 0; q < 4; q++) {
      const pair = (1 << q) | (1 << ((q + 1) % 4));
      if ((mask & pair) !== pair) continue;
      const [sx, sy] = quadrants[q];
      pixels[point(center, center)] = 0;
      pixels[point(center + sx, center + sy)] = 1;
    }
  }
  return pixels;
}

/** Merge matching runs vertically once, so long arms remain single rectangles. */
function compile(pixels: Uint8Array): readonly WireRect[] {
  const result: WireRect[] = [];
  let previous = new Map<string, [number, number, number, number]>();
  for (let y = 0; y <= WIRE_PIXEL_GRID; y++) {
    const next = new Map<string, [number, number, number, number]>();
    for (let x = 0; y < WIRE_PIXEL_GRID && x < WIRE_PIXEL_GRID; x++) {
      if (!pixels[point(x, y)]) continue;
      const start = x;
      while (x + 1 < WIRE_PIXEL_GRID && pixels[point(x + 1, y)]) x++;
      const width = x - start + 1,
        key = `${start}:${width}`;
      const run = previous.get(key);
      if (run) {
        run[3]++;
        previous.delete(key);
        next.set(key, run);
      } else next.set(key, [start, y, width, 1]);
    }
    result.push(...previous.values());
    previous = next;
  }
  return result;
}

// Authoring happens in logical pixels. These shared masks are compiled once;
// rendering uses the same small rectangle set in the editor, atlas and previews.
export const wirePixelGeometry = Array.from({ length: 16 }, (_, mask) =>
  compile(wireTopology(mask)),
);
export const cablePixelGeometry = Array.from({ length: 16 }, (_, mask) =>
  compile(topology(mask, 5, 1)),
);
export const cableStripePixelGeometry = Array.from({ length: 16 }, (_, mask) =>
  compile(stripe(mask)),
);
